// src/pages/MorePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { FaChevronRight, FaSearch } from "react-icons/fa";
import { getStoredUser } from "../utils/impersonation";
import { canAccessRoute } from "../utils/rbac";
import { APP_ESCAPE_EVENT, APP_SHORTCUT_EVENT, focusAndSelect } from "../utils/shortcuts";

const CARDS = [
  {
    title: "Dashboard",
    path: "/dashboard",
    desc: "Key metrics & active orders",
    category: "Sales",
  },
  {
    title: "Hourly Sales",
    path: "/reports/hourly-sales",
    desc: "Heatmap by hour/date",
    category: "Reports",
  },
  {
    title: "Top Products",
    path: "/reports/top-products",
    desc: "Quantity + gross leaders",
    category: "Reports",
  },
  {
    title: "Top Waiters",
    path: "/reports/top-waiters",
    desc: "Waiter performance summary",
    category: "Reports",
  },
  {
    title: "Category Performance",
    path: "/reports/category-performance",
    desc: "Category mix + contribution",
    category: "Reports",
  },
  {
    title: "Table Utilization",
    path: "/reports/table-utilization",
    desc: "Table stats for today",
    category: "Reports",
  },
  {
    title: "Table-wise Sales List",
    path: "/reports/table-wise-sales-list",
    desc: "Bills grouped by table",
    category: "Reports",
  },
  {
    title: "Table-wise Product Sales",
    path: "/reports/table-wise-product-sales",
    desc: "Product count by selected tables",
    category: "Reports",
  },
  {
    title: "Finance Tracker",
    path: "/finance/tracker",
    desc: "Expense/income tracker",
    category: "Finance",
  },
  {
    title: "Finance Report",
    path: "/finance/report",
    desc: "Detailed finance report + download",
    category: "Finance",
  },
  {
    title: "Profit & Loss Report",
    path: "/finance/profit-loss",
    desc: "Sales and other income minus expenses",
    category: "Finance",
  },
  {
    title: "Day Book Entry Report",
    path: "/finance/day-book",
    desc: "Day-wise sales, income and expenses",
    category: "Finance",
  },
  {
    title: "Khatabook",
    path: "/khatabook",
    desc: "Ledger view for parties",
    category: "Finance",
  },
  {
    title: "Party Bills",
    path: "/party-bills",
    desc: "Party wise invoice list",
    category: "Parties",
  },
  {
    title: "Menu Scanner",
    path: "/menu-scanner",
    desc: "AI menu scanning",
    category: "Operations",
  },
  {
    title: "QR Management",
    path: "/qr/management",
    desc: "Generate + manage QR codes",
    category: "Operations",
  },
  {
    title: "Support Chat",
    path: "/support",
    desc: "Chat with BillSarthi support",
    category: "Help",
  },
  {
    title: "Deleted Orders",
    path: "/deleted-orders",
    desc: "Restore removed bills quickly",
    category: "Sales",
  },
  {
    title: "Help Center",
    path: "/help",
    desc: "Quick answers + FAQs",
    category: "Help",
  },
  {
    title: "Keyboard Shortcuts",
    path: "/keyboard-shortcuts",
    desc: "Understand all web and desktop keyboard combos",
    category: "Help",
  },
  {
    title: "Login Help",
    path: "/login-help",
    desc: "Troubleshooting for signup/login",
    category: "Help",
  },
  {
    title: "Policies",
    path: "/policies",
    desc: "Refund, privacy and terms",
    category: "Help",
  },
  {
    title: "Printer / POS Settings",
    path: "/printer-settings",
    desc: "Printing, billing, and POS preferences",
    category: "Operations",
  },
  {
    title: "Product Reorder",
    path: "/product-reorder",
    desc: "Change product display order",
    category: "Operations",
  },
  {
    title: "Referral",
    path: "/referral",
    desc: "Invite and redeem referral rewards",
    category: "Growth",
  },
  {
    title: "Feedback & Bugs",
    path: "/feedback",
    desc: "Report bugs or requests",
    category: "Help",
  },
  {
    title: "Reset Account",
    path: "/reset-account",
    desc: "Danger zone: clear business data",
    category: "Admin",
  },
];

const MorePage = () => {
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(true);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);
  const user = getStoredUser();
  const accessibleCards = useMemo(
    () => CARDS.filter((card) => canAccessRoute(card.path, user)),
    [user]
  );

  const filteredCards = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accessibleCards;
    return accessibleCards.filter((card) => {
      const hay = `${card.title} ${card.desc} ${card.category} ${card.path}`.toLowerCase();
      return hay.includes(q);
    });
  }, [accessibleCards, query]);

  useEffect(() => {
    const onShortcut = (event) => {
      switch (event?.detail?.id) {
        case "more.search":
          event.preventDefault();
          setShowSearch(true);
          setTimeout(() => {
            focusAndSelect(searchInputRef.current);
          }, 50);
          return;
        case "more.open-first-result":
          if (
            !filteredCards.length ||
            (!query.trim() && document.activeElement !== searchInputRef.current)
          ) {
            return;
          }
          event.preventDefault();
          navigate(filteredCards[0].path);
          return;
        default:
          return;
      }
    };

    window.addEventListener(APP_SHORTCUT_EVENT, onShortcut);
    return () => window.removeEventListener(APP_SHORTCUT_EVENT, onShortcut);
  }, [filteredCards, navigate, query]);

  useEffect(() => {
    const onEscape = (event) => {
      const isFocused = document.activeElement === searchInputRef.current;
      if (!showSearch && !isFocused && !query.trim()) return;
      if (!isFocused && !query.trim() && showSearch) {
        event.preventDefault();
        setShowSearch(false);
        return;
      }
      if (isFocused || query.trim()) {
        event.preventDefault();
        searchInputRef.current?.blur?.();
        if (query.trim()) setQuery("");
      }
    };

    window.addEventListener(APP_ESCAPE_EVENT, onEscape);
    return () => window.removeEventListener(APP_ESCAPE_EVENT, onEscape);
  }, [query, showSearch]);

  return (
    <Page>
      <Inner>
        {/* Simple, very compact header */}
        <HeaderRow>
          <div>
            <HeaderTitle>Quick Access</HeaderTitle>
            <HeaderSubtitle>
              Jump directly to important tools and reports from a compact list
              of shortcuts.
            </HeaderSubtitle>
          </div>
          <HeaderActions>
            <SearchBtn
              type="button"
              title="Search shortcuts"
              onClick={() => setShowSearch((s) => !s)}
            >
              <FaSearch size={14} />
              <span>Search</span>
            </SearchBtn>
          </HeaderActions>
        </HeaderRow>

        {showSearch && (
          <SearchWrap>
            <SearchInput
              ref={searchInputRef}
              data-shortcut-search="page"
              type="text"
              placeholder="Search shortcuts… (e.g., add)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </SearchWrap>
        )}

        <CardGrid>
          {filteredCards.map((card) => (
            <Card key={card.title} onClick={() => navigate(card.path)}>
              <Meta>
                <Chip>{card.category}</Chip>
                <IconCircle>
                  <FaChevronRight size={10} />
                </IconCircle>
              </Meta>
              <Title>{card.title}</Title>
              <Desc>{card.desc}</Desc>
            </Card>
          ))}
        </CardGrid>
      </Inner>
    </Page>
  );
};

export default MorePage;

/* -------- styled -------- */

const Page = styled.div`
  min-height: 100vh;
  background: #f3f4f8;
  padding: 12px 12px 16px;
  display: flex;
  justify-content: center;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1100px;
`;

const HeaderRow = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  color: #0f172a;
`;

const HeaderSubtitle = styled.p`
  margin: 2px 0 0;
  font-size: 0.86rem;
  color: #6b7280;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const SearchBtn = styled.button`
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 700;
  font-size: 12px;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
  }
`;

const SearchWrap = styled.div`
  margin-bottom: 10px;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 520px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  outline: none;
  background: #fff;
  &:focus {
    border-color: #94a3b8;
    box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.25);
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
`;

const Card = styled.button`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
    border-color: #c7d2fe;
    background: #f9fafb;
  }
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Chip = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #1e3a8a;
  padding: 3px 8px;
  border-radius: 999px;
  background: #e0ebff;
`;

const IconCircle = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef2ff;
  color: #1f2933;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.98rem;
  color: #0f172a;
  font-weight: 700;
`;

const Desc = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 0.82rem;
  line-height: 1.35;
`;
